import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { login } from "../../redux/actions/grievanceActions1";
import "../../styles/updatedlogin.css";
import LoginCaptcha from "./LoginCaptcha";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.grievances);
  const captchaRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoginLoading = Boolean(isSubmitting || loading);

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [usernameReadOnly, setUsernameReadOnly] = useState(true);
  const [passwordReadOnly, setPasswordReadOnly] = useState(true);
  // Unique names each page load — browser cannot map saved credentials to fixed field names
  const [fieldNames] = useState(() => ({
    user: `fld_u_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    pass: `fld_p_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  }));

  // Add animation class when component mounts
  useEffect(() => {
    const card = document.querySelector(".login-card");
    if (card) {
      setTimeout(() => {
        card.classList.add("login-card-visible");
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (error) {
      if (typeof error === "object" && error.rData && error.rData.rMessage) {
        toast.error(error.rData.rMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.error(typeof error === "string" ? error : "An error occurred", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear errors when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = "Email is required";
      toast.error("Email is required", {
        position: "top-right",
        autoClose: 3000,
      });
    } else if (!/\S+@\S+\.\S+/.test(formData.username)) {
      errors.username = "Email is invalid";
      toast.error("Email is invalid", {
        position: "top-right",
        autoClose: 3000,
      });
    }

    if (!formData.password) {
      errors.password = "Password is required";
      toast.error("Password is required", {
        position: "top-right",
        autoClose: 3000,
      });
    } else if (formData.password.length < 2) {
      errors.password = "Password must be at least 2 characters";
      toast.error("Password must be at least 2 characters", {
        position: "top-right",
        autoClose: 3000,
      });
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Frontend captcha check — does not alter login / validate logic above
    if (captchaRef.current && !captchaRef.current.validate()) {
      toast.error("Please enter a valid captcha", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await dispatch(
        login({
          username: formData.username,
          password: formData.password,
        })
      );

      if (response.rCode === 0) {
        // Show success toast
        toast.success(response.rMessage, {
          position: "top-right",
          autoClose: 3000,
        });
        navigate("/Dashboard");
      } else {
        // Login failed
        toast.error(response.rMessage, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Login failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="login-background">
        <div className="login-shapes">
          <div className="shape-circle"></div>
          <div className="shape-square"></div>
          <div className="shape-triangle"></div>
        </div>
      </div>

      {/* Login Card with All-Side Borders */}
      <div className="login-card">
        {/* Logo Section */}
        <div style={{flex:1}} className="">
          <div className="">
            <img
              src="/images/AR_logo.png"
              alt="ARESA Logo"
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </div>
        </div>

          {/* Right side - Login form */}
          <div className="login-right">
            <div className="login-header">
              <h1>Sign In</h1>
            </div>

            <form
              className="login-form"
              onSubmit={handleSubmit}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
            >
              <div className="form-group">
                <div className="input-field">
                  <div className="input-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <input
                    id={fieldNames.user}
                    name={fieldNames.user}
                    type="text"
                    className={formErrors.username ? "error" : ""}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    readOnly={usernameReadOnly}
                    onFocus={() => setUsernameReadOnly(false)}
                    placeholder="Enter login ID"
                    value={formData.username}
                    onChange={(e) =>
                      handleChange({
                        target: { name: "username", value: e.target.value },
                      })
                    }
                  />
                </div>
                {formErrors.username && (
                  <div className="error-message">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{formErrors.username}</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <div className="input-field">
                  <div className="input-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="3"
                        y="11"
                        width="18"
                        height="11"
                        rx="2"
                        ry="2"
                      ></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </div>
                  {/*
                    Permanent anti-autofill: never use type="password".
                    Browsers detect type=password and force save/autofill,
                    ignoring autocomplete="off". Mask via CSS instead.
                  */}
                  <input
                    id={fieldNames.pass}
                    name={fieldNames.pass}
                    type="text"
                    className={`${formErrors.password ? "error" : ""} ${
                      showPassword ? "" : "password-masked"
                    }`.trim()}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    readOnly={passwordReadOnly}
                    onFocus={() => setPasswordReadOnly(false)}
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) =>
                      handleChange({
                        target: { name: "password", value: e.target.value },
                      })
                    }
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <div className="error-message">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{formErrors.password}</span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <LoginCaptcha ref={captchaRef} />
              </div>

              <button type="submit" className="login-button" disabled={isLoginLoading}>
                {isLoginLoading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </>
                )}
              </button>
            </form>

            <div className="login-footer">
              <p>© 2025 SourceDOTcom Pvt. Ltd.</p>
            </div>
          </div>
        </div>
      </div>
  );
};

export default Login;